// src/components/DashboardLayout.tsx - Updated to show project count instead of collection count
import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Folder as ProjectsIcon,  // Using same icon, but now represents projects
  Description as DocumentsIcon,
  Support as SupportIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, logout, refreshUserProfile } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Sidebar state - collapsed by default on mobile, expanded on desktop
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuItems = [
    { text: 'My Projects', icon: <ProjectsIcon />, path: '/dashboard/projects' },  // Changed from 'My Collections' to 'My Projects'
    { text: 'My Documents', icon: <DocumentsIcon />, path: '/dashboard/documents' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/dashboard/settings' },
    { text: 'Support', icon: <SupportIcon />, path: '/dashboard/support' },
  ];

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get display name with fallbacks
  const getDisplayName = () => {
    if (userProfile?.display_name) return userProfile.display_name;
    if (currentUser?.displayName) return currentUser.displayName;
    return 'User';
  };

  // Get user identifier for subtitle
  const getUserIdentifier = () => {
    if (userProfile?.email) return userProfile.email;
    if (currentUser?.email) return currentUser.email;
    return 'Anonymous';
  };

  // Get subscription tier color
  const getSubscriptionColor = (tier?: string) => {
    switch (tier) {
      case 'pro':
        return theme.palette.warning.main;
      case 'enterprise':
        return theme.palette.error.main;
      case 'free':
      default:
        return theme.palette.grey[500];
    }
  };

  // Get project count display (changed from collection count)
  const getProjectCountDisplay = () => {
    const count = userProfile?.project_count ?? 0;  // Changed from collection_count to project_count
    if (count === 0) return 'No projects';
    return `${count} project${count !== 1 ? 's' : ''}`;
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
          minHeight: 64,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'white',
            display: isCollapsed && !isMobile ? 'none' : 'block',
          }}
        >
          Field<span style={{ color: theme.palette.primary.main }}> Sense</span> AI
        </Typography>
        {isCollapsed && !isMobile && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: '1.5rem',
            }}
          >
            FS
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* User Profile Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: theme.palette.primary.main,
            fontSize: '1rem',
          }}
          src={userProfile?.photo_url || currentUser?.photoURL || undefined}
        >
          {getDisplayName()[0]?.toUpperCase() || 'U'}
        </Avatar>
        {(!isCollapsed || isMobile) && (
          <Box sx={{ ml: 2, minWidth: 0, flex: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {getDisplayName()}
              </Typography>
              {userProfile?.subscription_tier && userProfile.subscription_tier !== 'free' && (
                <Chip
                  label={userProfile.subscription_tier.toUpperCase()}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    bgcolor: getSubscriptionColor(userProfile.subscription_tier),
                    color: 'white',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {getUserIdentifier()}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.7rem',
              }}
            >
              {getProjectCountDisplay()}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleMenuItemClick(item.path)}
              selected={location.pathname === item.path}
              sx={{
                minHeight: 48,
                justifyContent: isCollapsed && !isMobile ? 'center' : 'initial',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isCollapsed && !isMobile ? 'auto' : 3,
                  justifyContent: 'center',
                  color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.8)',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {(!isCollapsed || isMobile) && (
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.9)',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* Logout Button */}
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 48,
              justifyContent: isCollapsed && !isMobile ? 'center' : 'initial',
              px: 2.5,
              '&:hover': {
                backgroundColor: 'rgba(244,67,54,0.2)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isCollapsed && !isMobile ? 'auto' : 3,
                justifyContent: 'center',
                color: '#f44336',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {(!isCollapsed || isMobile) && (
              <ListItemText
                primary="Logout"
                sx={{
                  '& .MuiListItemText-primary': {
                    color: '#f44336',
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>

      {/* Collapse Toggle Button (Desktop Only) */}
      {!isMobile && (
        <Box sx={{ p: 1 }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              width: '100%',
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar for Mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            bgcolor: '#222222',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
              Field<span style={{ color: theme.palette.primary.main }}>Sense</span> AI
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: isMobile ? 0 : isCollapsed ? collapsedDrawerWidth : drawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#222222',
              color: 'white',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
              bgcolor: '#222222',
              color: 'white',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            md: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)`,
          },
          mt: { xs: 8, md: 0 }, // Account for mobile app bar
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;